/**
 * Pipe estimating & takeoff engine.
 *
 * Turns a Pipe Sketch takeoff (cut list + fittings + valves + supports + terminations)
 * into labor hours and material dollars.
 *
 * Everything here is deterministic and ships in the bundle — no network, no lookups.
 * The labor units are industry-typical field units for an experienced crew; the
 * material prices are seed values. BOTH are editable per line in the UI and the
 * overrides are stored on the device.
 */

import {
  getSizeSpec,
  type ComponentKind,
  type MaterialId,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";
import {
  componentCost,
  fittingCost,
  pipeCostPerFt,
  supportCost,
  terminationCost,
  plumbingCost,
  hvacCost,
  generalCost,
  structuralCost,
  electricalCost,
} from "@/lib/material-catalog";
import { getPlumbingItem } from "@/lib/plumbing-data";
import { getHvacItem, hvacUnitLabel } from "@/lib/hvac-data";
import { getGeneralItem, generalUnitLabel } from "@/lib/general-data";
import { getStructuralItem, structuralUnitLabel } from "@/lib/structural-data";
import { getElectricalItem } from "@/lib/electrical-takeoff";
import type {
  ComponentPlacement,
  CutPiece,
  RunFitting,
  SupportPlacement,
  TerminationPlacement,
} from "@/hooks/use-pipe-sketch";

/* ------------------------------------------------------------------ */
/* settings                                                            */
/* ------------------------------------------------------------------ */

export interface EstimateSettings {
  /** fully burdened crew rate, $ per labor hour */
  laborRate: number;
  /** productivity factor — 1.00 = standard conditions, >1 = harder job */
  laborFactor: number;
  /** crew size used to convert hours into calendar days */
  crewSize: number;
  /** working hours in a shift */
  hoursPerDay: number;
  /** scrap / offcut allowance added to pipe footage, % */
  pipeWastePct: number;
  /** global material price adjustment, % (mill swings, region) */
  priceIndexPct: number;
  /** markup applied to material only, % */
  materialMarkupPct: number;
  /** crew rate for Structural lines — falls back to the mechanical rate */
  structuralRate?: number;
  /** crew rate for Electrical lines — falls back to the mechanical rate */
  electricalRate?: number;
  /** crew rate for HVAC / sheet metal lines, $ per hour */
  hvacRate?: number;
  /** sales tax on material, % */
  taxPct: number;
  /** overhead on labor + material, % */
  overheadPct: number;
  /** profit on the subtotal, % */
  profitPct: number;
  /** consumables (rod, gas, wheels, rags) as % of labor cost */
  consumablesPct: number;
}

export const DEFAULT_ESTIMATE_SETTINGS: EstimateSettings = {
  laborRate: 95,
  laborFactor: 1,
  crewSize: 2,
  hoursPerDay: 8,
  pipeWastePct: 5,
  priceIndexPct: 0,
  materialMarkupPct: 15,
  taxPct: 0,
  overheadPct: 10,
  profitPct: 10,
  consumablesPct: 4,
};

/** Per-line user overrides, keyed by the line key. */
export interface UnitOverride {
  unitHours?: number;
  unitCost?: number;
}
export type OverrideMap = Record<string, UnitOverride>;

/* ------------------------------------------------------------------ */
/* interpolation                                                       */
/* ------------------------------------------------------------------ */

type SizeTable = Record<number, number>;

/** Linear interpolation across a size-keyed table, clamped at both ends. */
function bySize(table: SizeTable, nom: number): number {
  const keys = Object.keys(table)
    .map(Number)
    .sort((a, b) => a - b);
  if (!keys.length) return 0;
  if (nom <= keys[0]) return table[keys[0]];
  if (nom >= keys[keys.length - 1]) return table[keys[keys.length - 1]];
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (nom >= a && nom <= b) {
      const t = (nom - a) / (b - a);
      return table[a] + t * (table[b] - table[a]);
    }
  }
  return table[keys[keys.length - 1]];
}

/* ------------------------------------------------------------------ */
/* labor unit tables (crew hours)                                      */
/* ------------------------------------------------------------------ */

/** Handle, rig and erect straight pipe — hours per linear foot. */
const PIPE_PER_FT: SizeTable = {
  0.5: 0.1,
  0.75: 0.11,
  1: 0.12,
  1.25: 0.14,
  1.5: 0.15,
  2: 0.17,
  2.5: 0.21,
  3: 0.25,
  4: 0.31,
  5: 0.38,
  6: 0.44,
  8: 0.57,
  10: 0.72,
  12: 0.86,
  14: 0.99,
  16: 1.14,
  18: 1.3,
  20: 1.46,
  24: 1.8,
};

/** Full-penetration field butt weld including fit-up and tack — hours per joint. */
const BW_JOINT: SizeTable = {
  0.5: 0.7,
  0.75: 0.8,
  1: 0.9,
  1.25: 1.0,
  1.5: 1.1,
  2: 1.3,
  2.5: 1.6,
  3: 1.9,
  4: 2.5,
  5: 3.05,
  6: 3.6,
  8: 4.8,
  10: 6.2,
  12: 7.5,
  14: 8.7,
  16: 10,
  18: 11.5,
  20: 13,
  24: 16,
};

/** Cut, ream, clean, flux and solder — hours per joint. */
const SOLDER_JOINT: SizeTable = {
  0.5: 0.18,
  0.75: 0.2,
  1: 0.24,
  1.25: 0.3,
  1.5: 0.35,
  2: 0.45,
  2.5: 0.6,
  3: 0.75,
  4: 1.05,
  6: 1.7,
  8: 2.5,
};

/** Cut, deburr, prime and solvent weld — hours per joint. */
const SOLVENT_JOINT: SizeTable = {
  0.5: 0.1,
  0.75: 0.11,
  1: 0.13,
  1.25: 0.16,
  1.5: 0.18,
  2: 0.22,
  2.5: 0.3,
  3: 0.36,
  4: 0.5,
  6: 0.8,
  8: 1.15,
  10: 1.5,
  12: 1.9,
};

/** Handle and set a fitting, excluding its joints — hours each. */
const FITTING_SET: SizeTable = {
  0.5: 0.15,
  1: 0.2,
  2: 0.3,
  3: 0.45,
  4: 0.6,
  6: 0.9,
  8: 1.25,
  10: 1.6,
  12: 2.0,
  16: 2.8,
  20: 3.6,
  24: 4.4,
};

/** Rig, set and bolt up a valve or specialty, excluding pipe joints — hours each. */
const VALVE_SET: SizeTable = {
  0.5: 0.5,
  1: 0.7,
  2: 1.1,
  3: 1.6,
  4: 2.2,
  6: 3.4,
  8: 4.8,
  10: 6.2,
  12: 7.6,
  16: 10.5,
  20: 13.5,
  24: 17,
};

/** Lay out, drill, hang and adjust a support — hours each. */
const SUPPORT_SET: SizeTable = {
  0.5: 0.6,
  1: 0.7,
  2: 0.9,
  3: 1.1,
  4: 1.4,
  6: 1.9,
  8: 2.5,
  10: 3.1,
  12: 3.8,
  16: 5.0,
  20: 6.2,
  24: 7.5,
};

/* multipliers ------------------------------------------------------- */

const FITTING_LABOR_MULT: Record<string, number> = {
  elbow90: 1,
  elbow45: 1,
  tee: 1.4,
  reducer: 0.9,
};

const COMPONENT_LABOR_MULT: Record<ComponentKind, number> = {
  gate: 1,
  ball: 0.9,
  globe: 1.05,
  check: 0.85,
  butterfly: 0.8,
  flangePair: 0.6,
  blindFlange: 0.5,
  union: 0.4,
  strainer: 1.1,
};

const SUPPORT_LABOR_MULT: Record<SupportKind, number> = {
  hanger: 1,
  trapeze: 2.1,
  guide: 1.3,
  anchor: 1.8,
  spring: 1.6,
  shoe: 1.4,
  roller: 1.5,
  riserClamp: 0.9,
};

const TERMINATION_LABOR_MULT: Record<TerminationKind, number> = {
  cap: 0.5,
  flangedEnd: 0.7,
  blindEnd: 0.7,
  pumpNozzle: 1.6,
  vesselNozzle: 1.4,
  exchangerNozzle: 1.4,
  tankNozzle: 1.4,
  equipment: 1.5,
  openEnd: 0.1,
  futureConn: 0.3,
};

/** Welding stainless is slower; heavy wall is slower. */
const JOINT_LABOR_MULT: Record<MaterialId, number> = {
  "cs-a106-sch40": 1,
  "cs-a106-sch80": 1.25,
  "ss-304-sch10s": 1.45,
  "ss-304-sch40s": 1.7,
  "cu-type-l": 1,
  "pvc-sch40": 1,
};

/* ------------------------------------------------------------------ */
/* line items                                                          */
/* ------------------------------------------------------------------ */

export type EstimateSection =
  | "Pipe"
  | "Joints"
  | "Fittings"
  | "Valves & specialties"
  | "Supports"
  | "Terminations"
  | "Plumbing"
  | "HVAC"
  | "Structural"
  | "Electrical"
  | "General";

export const ESTIMATE_SECTIONS: EstimateSection[] = [
  "Pipe",
  "Joints",
  "Fittings",
  "Valves & specialties",
  "Supports",
  "Terminations",
  "Plumbing",
  "HVAC",
  "Structural",
  "Electrical",
  "General",
];

export interface EstimateLine {
  /** stable key used for per-line overrides */
  key: string;
  section: EstimateSection;
  description: string;
  size: string;
  qty: number;
  unit: string;
  /** default labor hours per unit before any override */
  baseHours: number;
  /** default material $ per unit before any override */
  baseCost: number;
  /** effective values after overrides + settings */
  unitHours: number;
  unitCost: number;
  hours: number;
  material: number;
  labor: number;
  total: number;
  edited: boolean;
}

export interface EstimateTotals {
  hours: number;
  laborCost: number;
  consumables: number;
  materialBase: number;
  markup: number;
  tax: number;
  materialCost: number;
  subtotal: number;
  overhead: number;
  profit: number;
  grandTotal: number;
  crewDays: number;
  costPerFoot: number;
  totalFeet: number;
}

export interface EstimateResult {
  lines: EstimateLine[];
  totals: EstimateTotals;
}

export interface EstimateInput {
  material: MaterialId;
  cutList: CutPiece[];
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  supports: SupportPlacement[];
  terminations: TerminationPlacement[];
  settings: EstimateSettings;
  overrides: OverrideMap;
  /** plumbing catalog item id → quantity (feet for pipe, each otherwise) */
  plumbing?: Record<string, number>;
  /** hvac catalog item id → quantity (feet/sq ft for duct, each otherwise) */
  hvac?: Record<string, number>;
  /** structural catalog item id → quantity (cu yd / ft / lb / sq ft / each) */
  structural?: Record<string, number>;
  /** electrical catalog key → quantity (feet of conduit/wire, each for gear) */
  electrical?: Record<string, number>;
  /** general job-cost item id → quantity (each / days / weeks / hours / miles) */
  general?: Record<string, number>;
}

/* ------------------------------------------------------------------ */

const jointTable = (material: MaterialId): SizeTable => {
  if (material === "cu-type-l") return SOLDER_JOINT;
  if (material === "pvc-sch40") return SOLVENT_JOINT;
  return BW_JOINT;
};

export function jointWord(material: MaterialId) {
  if (material === "cu-type-l") return "Soldered joint";
  if (material === "pvc-sch40") return "Solvent weld joint";
  return "Field butt weld";
}

interface Bucket {
  key: string;
  section: EstimateSection;
  description: string;
  size: string;
  unit: string;
  qty: number;
  baseHours: number;
  baseCost: number;
}

export function buildEstimate(input: EstimateInput): EstimateResult {
  const { material, cutList, fittings, placements, supports, terminations, settings, overrides } =
    input;
  const plumbing = input.plumbing ?? {};
  const hvac = input.hvac ?? {};
  const structural = input.structural ?? {};
  const electrical = input.electrical ?? {};
  const general = input.general ?? {};

  const priceIndex = 1 + settings.priceIndexPct / 100;
  const jointMult = JOINT_LABOR_MULT[material] ?? 1;
  const jt = jointTable(material);

  const buckets = new Map<string, Bucket>();
  const push = (b: Bucket) => {
    const existing = buckets.get(b.key);
    if (existing) existing.qty += b.qty;
    else buckets.set(b.key, { ...b });
  };

  /* --- pipe, by size ------------------------------------------------ */
  const waste = 1 + settings.pipeWastePct / 100;
  for (const p of cutList) {
    const spec = getSizeSpec(material, p.size);
    push({
      key: `pipe:${p.size}`,
      section: "Pipe",
      description: `${p.size} pipe`,
      size: p.size,
      unit: "ft",
      qty: (p.cutLength / 12) * waste,
      baseHours: bySize(PIPE_PER_FT, spec.nom),
      baseCost: pipeCostPerFt(material, p.size) * priceIndex,
    });
  }

  /* --- joints, by size ---------------------------------------------- */
  const addJoints = (size: string, n: number) => {
    if (n <= 0) return;
    const spec = getSizeSpec(material, size);
    push({
      key: `joint:${size}`,
      section: "Joints",
      description: `${size} ${jointWord(material).toLowerCase()}`,
      size,
      unit: "ea",
      qty: n,
      baseHours: bySize(jt, spec.nom) * jointMult,
      baseCost: 0,
    });
  };
  for (const f of fittings) addJoints(f.size, f.welds);
  for (const c of placements) addJoints(c.size, c.joints);
  for (const t of terminations) addJoints(t.size, t.joints);

  /* --- fittings ------------------------------------------------------ */
  for (const f of fittings) {
    const spec = getSizeSpec(material, f.size);
    const mult = FITTING_LABOR_MULT[f.kind] ?? 1;
    push({
      key: `fit:${f.kind}:${f.size}${f.sizeOut ? `x${f.sizeOut}` : ""}`,
      section: "Fittings",
      description: f.label,
      size: f.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(FITTING_SET, spec.nom) * mult,
      baseCost: fittingCost(material, f.kind, f.size) * priceIndex,
    });
  }

  /* --- valves & specialties ------------------------------------------ */
  for (const c of placements) {
    const spec = getSizeSpec(material, c.size);
    push({
      key: `cmp:${c.kind}:${c.size}`,
      section: "Valves & specialties",
      description: `${c.size} ${c.label}`,
      size: c.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(VALVE_SET, spec.nom) * (COMPONENT_LABOR_MULT[c.kind] ?? 1),
      baseCost: componentCost(material, c.kind, c.size) * priceIndex,
    });
  }

  /* --- supports ------------------------------------------------------- */
  for (const s of supports) {
    const spec = getSizeSpec(material, s.size);
    push({
      key: `sup:${s.kind}:${s.size}`,
      section: "Supports",
      description: `${s.size} ${s.label}`,
      size: s.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(SUPPORT_SET, spec.nom) * (SUPPORT_LABOR_MULT[s.kind] ?? 1),
      baseCost: supportCost(material, s.kind, s.size) * priceIndex,
    });
  }

  /* --- terminations ---------------------------------------------------- */
  for (const t of terminations) {
    const spec = getSizeSpec(material, t.size);
    push({
      key: `term:${t.kind}:${t.size}`,
      section: "Terminations",
      description: `${t.size} ${t.label}`,
      size: t.size,
      unit: "ea",
      qty: 1,
      baseHours: bySize(VALVE_SET, spec.nom) * (TERMINATION_LABOR_MULT[t.kind] ?? 1),
      baseCost: terminationCost(material, t.kind, t.size) * priceIndex,
    });
  }

  /* --- plumbing pipe, valves, traps and fixtures ------------------------ */
  for (const [id, rawQty] of Object.entries(plumbing)) {
    const qty = Number(rawQty) || 0;
    const item = getPlumbingItem(id);
    if (!item || qty <= 0) continue;
    push({
      key: `plmb:${id}`,
      section: "Plumbing",
      description: item.size === "—" ? item.label : `${item.size} ${item.label}`,
      size: item.size,
      unit: item.unit,
      qty: item.unit === "ft" ? qty * waste : qty,
      baseHours: item.hours,
      baseCost: plumbingCost(id) * priceIndex,
    });
  }

  /* --- hvac duct, coils, registers and blowers -------------------------- */
  for (const [id, rawQty] of Object.entries(hvac)) {
    const qty = Number(rawQty) || 0;
    const item = getHvacItem(id);
    if (!item || qty <= 0) continue;
    push({
      key: `hvac:${id}`,
      section: "HVAC",
      description: item.size === "—" ? item.label : `${item.size} ${item.label}`,
      size: item.size,
      unit: hvacUnitLabel(item.unit),
      qty: item.unit === "ea" ? qty : qty * waste,
      baseHours: item.hours,
      baseCost: hvacCost(id) * priceIndex,
    });
  }

  /* --- structural concrete, rebar, steel and misc iron ------------------ */
  for (const [id, rawQty] of Object.entries(structural)) {
    const qty = Number(rawQty) || 0;
    const item = getStructuralItem(id);
    if (!item || qty <= 0) continue;
    push({
      key: `struct:${id}`,
      section: "Structural",
      description: item.size === "—" ? item.label : `${item.size} ${item.label}`,
      size: item.size,
      unit: structuralUnitLabel(item.unit),
      qty,
      baseHours: item.hours,
      baseCost: structuralCost(id) * priceIndex,
    });
  }

  /* --- electrical conduit, wire, panels, gear and devices --------------- */
  for (const [id, rawQty] of Object.entries(electrical)) {
    const qty = Number(rawQty) || 0;
    const item = getElectricalItem(id);
    if (!item || qty <= 0) continue;
    push({
      key: `elec:${id}`,
      section: "Electrical",
      description: item.size === "—" ? item.label : `${item.size} ${item.label}`,
      size: item.size,
      unit: item.unit,
      qty,
      baseHours: item.hours,
      baseCost: electricalCost(id) * priceIndex,
    });
  }

  /* --- general job costs: permits, rental, site, consumables, travel ---- */
  for (const [id, rawQty] of Object.entries(general)) {
    const qty = Number(rawQty) || 0;
    const item = getGeneralItem(id);
    if (!item || qty <= 0) continue;
    push({
      key: `gen:${id}`,
      section: "General",
      description: item.size === "—" ? item.label : `${item.size} ${item.label}`,
      size: item.size,
      unit: generalUnitLabel(item.unit),
      qty,
      baseHours: item.hours,
      baseCost: generalCost(id) * priceIndex,
    });
  }

  /* --- resolve ---------------------------------------------------------- */
  const order = new Map(ESTIMATE_SECTIONS.map((s, i) => [s, i]));
  const lines: EstimateLine[] = [...buckets.values()]
    .sort((a, b) => {
      const d = (order.get(a.section) ?? 9) - (order.get(b.section) ?? 9);
      return d !== 0 ? d : a.description.localeCompare(b.description);
    })
    .map((b) => {
      const ov = overrides[b.key];
      const unitHours = ov?.unitHours ?? b.baseHours;
      const unitCost = ov?.unitCost ?? b.baseCost;
      const hours = unitHours * b.qty * settings.laborFactor;
      const materialCost = unitCost * b.qty;
      const rate =
        b.section === "Structural" && settings.structuralRate
          ? settings.structuralRate
          : b.section === "Electrical" && settings.electricalRate
            ? settings.electricalRate
            : b.section === "HVAC" && settings.hvacRate
              ? settings.hvacRate
              : settings.laborRate;
      const labor = hours * rate;
      return {
        key: b.key,
        section: b.section,
        description: b.description,
        size: b.size,
        qty: b.qty,
        unit: b.unit,
        baseHours: b.baseHours,
        baseCost: b.baseCost,
        unitHours,
        unitCost,
        hours,
        material: materialCost,
        labor,
        total: labor + materialCost,
        edited: ov?.unitHours != null || ov?.unitCost != null,
      };
    });

  const hours = lines.reduce((s, l) => s + l.hours, 0);
  const laborCost = lines.reduce((s, l) => s + l.labor, 0);
  const consumables = laborCost * (settings.consumablesPct / 100);
  const materialBase = lines.reduce((s, l) => s + l.material, 0);
  const markup = materialBase * (settings.materialMarkupPct / 100);
  const tax = (materialBase + markup) * (settings.taxPct / 100);
  const materialCost = materialBase + markup + tax;
  const subtotal = laborCost + consumables + materialCost;
  const overhead = subtotal * (settings.overheadPct / 100);
  const profit = (subtotal + overhead) * (settings.profitPct / 100);
  const grandTotal = subtotal + overhead + profit;
  const totalFeet = lines
    .filter((l) => l.section === "Pipe")
    .reduce((s, l) => s + l.qty, 0);

  return {
    lines,
    totals: {
      hours,
      laborCost,
      consumables,
      materialBase,
      markup,
      tax,
      materialCost,
      subtotal,
      overhead,
      profit,
      grandTotal,
      crewDays:
        settings.crewSize > 0 && settings.hoursPerDay > 0
          ? hours / (settings.crewSize * settings.hoursPerDay)
          : 0,
      costPerFoot: totalFeet > 0 ? grandTotal / totalFeet : 0,
      totalFeet,
    },
  };
}

export const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

export const hrs = (n: number) => `${n.toFixed(2)} hr`;

/* ------------------------------------------------------------------ */
/* labor units exposed for the cut list                                */
/* ------------------------------------------------------------------ */

/** Handle, rig and erect straight pipe — crew hours per foot. */
export function pipeHoursPerFt(material: MaterialId, size: string): number {
  return bySize(PIPE_PER_FT, getSizeSpec(material, size).nom);
}

/** One field joint (weld, solder or solvent weld) — crew hours each. */
export function jointHoursEach(material: MaterialId, size: string): number {
  return (
    bySize(jointTable(material), getSizeSpec(material, size).nom) *
    (JOINT_LABOR_MULT[material] ?? 1)
  );
}
